import React, { useState, useEffect } from 'react';
import {
  LogOut, ShieldCheck, Zap, Lock, Trash2, Award,
  BookOpen, Flame, Edit2, Sparkles, ChevronRight,
  Heart, Target, Clock, Trophy, X
} from 'lucide-react';

import Mascot from '../components/Mascot';
import LevelExplorer from '../components/profile/LevelExplorer';
import useLevel from '../hooks/useLevel';
import useBadges from '../hooks/useBadges';
import useDetailedStats from '../hooks/useDetailedStats';
import useDailyQuests from '../hooks/useDailyQuests';


const LEVEL_RANGES = [
  { range: '1-10',   minLevel: 1,  name: 'Rango 1: Discovery',  color: 'from-emerald-400 to-green-500',  titles: ['Newcomer 🌱', 'Tourist 🎒', 'Explorer 🗺️'],        xp: '0 - 20,000 XP' },
  { range: '11-25',  minLevel: 11, name: 'Rango 2: Foundation', color: 'from-blue-400 to-indigo-500',    titles: ['Apprentice 🛠️', 'Pathfinder 🧭', 'Navigator ⛵'],  xp: '20,000 - 70,000 XP' },
  { range: '26-50',  minLevel: 26, name: 'Rango 3: Expansion',  color: 'from-violet-400 to-purple-500',  titles: ['Artisan 🎨', 'Communicator 💬', 'Speaker 🎙️'],     xp: '70,000 - 150,000 XP' },
  { range: '51-75',  minLevel: 51, name: 'Rango 4: Fluency',    color: 'from-amber-400 to-orange-500',   titles: ['Orator 🗣️', 'Scholar 📚', 'Diplomat 🤝'],          xp: '150,000 - 240,000 XP' },
  { range: '76-90',  minLevel: 76, name: 'Rango 5: Advanced',   color: 'from-rose-500 to-red-500',       titles: ['Linguist 🧠', 'Expert 🏅'],                         xp: '240,000 - 310,000 XP' },
  { range: '91-100', minLevel: 91, name: 'Rango 6: Master',     color: 'from-yellow-400 to-amber-500',   titles: ['Virtuoso 🎻', 'Grandmaster 🏛️', 'Legend 👑'],      xp: '310,000+ XP' },
];

const RING_CIRCUMFERENCE = 528;

export default function ProfileView({
  userProgress,
  handleLogout,
  onOpenTutorial,
  activateTeacherMode,
  setView,
  onGoToDiploma,
 setActiveModal,
  isTeacherMode,
  onGrantTeacherXP,
  heartLives,
  backpackLives,
  setPlacementMode,
}) {
  const [password, setPassword] = useState('');
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [mascotBubble, setMascotBubble] = useState(null);
  const [isJumping, setIsJumping] = useState(false);
  const [showLevelInfo, setShowLevelInfo] = useState(false);
  const [showLevelOverlay, setShowLevelOverlay] = useState(false);
  const [showBadgesOverlay, setShowBadgesOverlay] = useState(false);

  const lifetimeXP = userProgress?.lifetimeXp || userProgress?.xp || 0;
  const levelStats = useLevel(lifetimeXP);
  const userBadges = useBadges(userProgress);
  const stats = useDetailedStats(userProgress);
  const daily = useDailyQuests(userProgress);

  const currentSkin = userProgress?.currentSkin || 'bribark';
  const characterName = currentSkin.includes('dolly') ? 'DollyBelle' : 'Bribark';
  const streak = userProgress?.maxStreak || 0;
  const totalLives = (heartLives || 0) + (backpackLives || 0);

  useEffect(() => { return () => setMascotBubble(null); }, []);

  const handleAvatarClick = () => {
  setIsJumping(true);
  setTimeout(() => setIsJumping(false), 600);
  setTimeout(() => setActiveModal({ type: 'XP_STORE' }), 300);
};

  const handleUnlockAttempt = () => {
    if (password === 'EducaTMexico') {
      activateTeacherMode();
      setPassword('');
      setShowAdminPanel(false);
      setMascotBubble(null);
    } else {
      setMascotBubble(currentSkin.includes('dolly') ? "Sorry pero esta no es la contraseña." : "Access denied, amigo.");
      setPassword('');
      setTimeout(() => setMascotBubble(null), 3000);
    }
  };

  const handleResetProgress = () => {
    if (window.confirm("⚠️ ¿Estás seguro? Se borrará todo tu progreso.")) {
      localStorage.removeItem('educat_v3_progress');
      localStorage.removeItem('educat_teacher_mode');
      window.location.reload();
    }
  };

  const ringOffset = RING_CIRCUMFERENCE - (RING_CIRCUMFERENCE * levelStats.progressPercentage) / 100;

  return (
     <>
    <div className="space-y-5 animate-in fade-in slide-in-from-bottom-10 text-center pb-24 w-full max-w-2xl mx-auto px-4">

      {/* === 1. TARJETA HERO === */}
      <div className="bg-gradient-to-b from-[#4158D0] via-[#5865F2] to-[#7950f2] pt-10 pb-8 px-6 rounded-[3.5rem] shadow-2xl relative overflow-hidden border-b-8 border-[#3730a3]">
        <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/4 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-indigo-400/20 rounded-full translate-y-1/2 -translate-x-1/4 blur-2xl pointer-events-none" />

        <div className="relative z-10 flex flex-col items-center gap-4">
          <div className="relative cursor-pointer group" onClick={handleAvatarClick}>
           <svg width="192" height="192" className="transform -rotate-90 pointer-events-none">
              <defs>
                <linearGradient id="ringGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#63dcff" />
                  <stop offset="100%" stopColor="#a78bfa" />
                </linearGradient>
              </defs>
              <circle cx="96" cy="96" r="84" stroke="rgba(255,255,255,0.15)" strokeWidth="10" fill="transparent" />
              <circle cx="96" cy="96" r="84"
                stroke="url(#ringGrad)"
                strokeWidth="10"
                fill="transparent"
                strokeDasharray={RING_CIRCUMFERENCE}
                strokeDashoffset={ringOffset}
                strokeLinecap="round"
                style={{ transition: 'stroke-dashoffset 1s ease-out', filter: 'drop-shadow(0 0 8px rgba(99,220,255,0.7))' }}
              />
            </svg>

            <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ transform: 'scale(1.2)' }}>
  <Mascot type={currentSkin} isJumping={isJumping} />
</div>

            <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 bg-white text-[#5865F2] px-5 py-1.5 rounded-2xl font-black shadow-2xl border-2 border-indigo-100 flex items-center gap-1.5 whitespace-nowrap">
              <span className="text-[10px] font-black opacity-50 uppercase tracking-widest">LVL</span>
              <span className="text-lg">{levelStats.level}</span>
            </div>

            <button
              onClick={(e) => { e.stopPropagation(); handleAvatarClick(); }}
              className="absolute bottom-4 -right-4 bg-white p-2.5 rounded-full shadow-lg text-slate-400 hover:text-indigo-600 transition-all active:scale-90"
            >
              <Edit2 size={16} />
            </button>
          </div>

          <div className="mt-4 space-y-1">
            <h2 className="text-4xl font-black text-white drop-shadow-lg">{characterName}</h2>
            <p className="text-indigo-200 font-bold text-sm uppercase tracking-widest italic opacity-90">
              {levelStats.title}
            </p>
          </div>
        </div>
      </div>

      {/* === 2. STATS GRID === */}
      <div className="grid grid-cols-4 gap-2.5 -mt-6 relative z-20 px-1">
        {[
          { label: 'XP',         val: (userProgress.xp || 0).toLocaleString(), emoji: '⚡', bg: 'bg-yellow-50',  border: 'border-yellow-300', text: 'text-yellow-600' },
          { label: 'Corazones',  val: totalLives,                               emoji: '❤️', bg: 'bg-rose-50',   border: 'border-rose-300',   text: 'text-rose-600' },
          { label: 'Sets Listos',val: userProgress.completedSets?.length || 0,  emoji: '📚', bg: 'bg-emerald-50',border: 'border-emerald-300',text: 'text-emerald-600' },
          { label: 'Racha',      val: streak,                                   emoji: '🔥', bg: 'bg-orange-50', border: 'border-orange-300', text: 'text-orange-600' },
        ].map((s, i) => (
          <div key={i} className={`${s.bg} p-3 rounded-2xl shadow-lg border-2 ${s.border} border-b-4 flex flex-col items-center gap-0.5`}>
            <span className="text-xl">{s.emoji}</span>
            <span className={`text-sm font-black ${s.text} truncate w-full text-center leading-tight`}>{s.val}</span>
            <span className="text-[7px] font-black text-slate-400 uppercase tracking-tighter text-center leading-tight">{s.label}</span>
          </div>
        ))}
      </div>

      {/* === 3. NIVEL & PROGRESO === */}
      <div className="bg-white p-6 rounded-[2.5rem] shadow-sm border-2 border-slate-100 text-left space-y-4">
        <div className="flex justify-between items-end">
          <div>
            <h3 className="font-black text-slate-800 text-lg leading-none">Nivel & Progreso</h3>
            <p className="text-xs font-bold text-slate-400 mt-1">{levelStats.rankName}</p>
          </div>
          <span className="text-xs font-black text-indigo-600 bg-indigo-50 px-3 py-1 rounded-xl">
            {Math.round(levelStats.progressPercentage)}%
          </span>
        </div>

        <button className="w-full text-left space-y-2 group" onClick={() => setShowLevelOverlay(true)}>
          <div className="relative w-full h-7 bg-slate-100 rounded-full overflow-hidden p-1 shadow-inner border border-slate-200 group-hover:border-indigo-300 transition-all">
            <div
              className="h-full bg-gradient-to-r from-cyan-400 via-blue-500 to-indigo-600 rounded-full transition-all duration-1000 shadow-md"
              style={{ width: `${levelStats.progressPercentage}%` }}
            />
          </div>
          <p className="text-[10px] text-indigo-400 font-bold text-center group-hover:text-indigo-600 transition-colors">
            Toca para ver todos los niveles →
          </p>
        </button>

        <div className="flex justify-between items-center text-[11px] font-bold uppercase tracking-wider pt-1 border-t border-slate-50">
          <span className="text-slate-400">{levelStats.currentLevelXP.toLocaleString()} / {levelStats.nextLevelXP.toLocaleString()} XP</span>
          <span className="text-slate-700">Sig: {levelStats.nextTitle}</span>
        </div>
      </div>

      {/* === 4. LOGROS === */}
      <div className="bg-white p-6 rounded-[2.5rem] shadow-sm border-2 border-slate-100">
        <div className="flex justify-between items-center mb-5">
          <h3 className="font-black text-slate-800 text-base flex items-center gap-2">
            <Trophy size={20} className="text-amber-500" /> Logros
          </h3>
          <button
            onClick={() => setShowBadgesOverlay(true)}
            className="text-[10px] font-black text-indigo-500 bg-indigo-50 px-3 py-1.5 rounded-full hover:bg-indigo-100 transition-all"
          >
            Ver todos ({userBadges.length})
          </button>
        </div>

        <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
          {userBadges.filter(b => b.isUnlocked).length === 0 ? (
            <p className="text-sm text-slate-400 italic w-full text-center py-2">¡Juega para desbloquear logros!</p>
          ) : (
            userBadges.filter(b => b.isUnlocked).map((badge) => (
              <button key={badge.id} onClick={() => setShowBadgesOverlay(true)} className="flex flex-col items-center shrink-0 group">
              <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${badge.currentGradient} 
  flex items-center justify-center 
  ring-4 ring-white
  border-4 border-yellow-300
  relative overflow-hidden
  group-hover:scale-110 transition-all
  shadow-[0_6px_20px_rgba(0,0,0,0.3)]`}>
  {/* Brillo superior */}
  <div className="absolute top-0 left-0 w-full h-1/2 bg-white/40 rounded-t-full pointer-events-none" />
  {/* Brillo punto */}
  <div className="absolute top-1 left-3 w-3 h-3 bg-white/60 rounded-full blur-sm pointer-events-none" />
  <span className="text-2xl drop-shadow-md relative z-10">{badge.icon}</span>
</div>
                <p className="text-[8px] font-black text-slate-500 mt-2 text-center w-16 truncate uppercase">{badge.currentTierName}</p>
              </button>
            ))
          )}
        </div>
      </div>

      {/* === 5. ESTADÍSTICAS === */}
      <div className="bg-white p-6 rounded-[2.5rem] shadow-sm border-2 border-slate-100">
        <h3 className="font-black text-slate-800 mb-6 text-left text-base">Estadísticas Detalladas</h3>
        <div className="grid grid-cols-3 divide-x-2 divide-slate-100">
          {[
            { icon: <Target size={22} />,   val: stats.accuracy,           label: 'Precisión', bg: 'bg-rose-50',    text: 'text-rose-500' },
            { icon: <BookOpen size={22} />, val: stats.completedLessons,   label: 'Lecciones', bg: 'bg-emerald-50', text: 'text-emerald-600' },
            { icon: <Clock size={22} />,    val: stats.studyTimeFormatted, label: 'Tiempo',    bg: 'bg-blue-50',    text: 'text-blue-500' },
          ].map((s, i) => (
            <div key={i} className="flex flex-col items-center px-2 gap-2">
              <div className={`w-12 h-12 ${s.bg} ${s.text} rounded-2xl flex items-center justify-center`}>{s.icon}</div>
              <span className="text-2xl font-black text-slate-800 leading-none">{s.val}</span>
              <span className="text-[9px] font-black text-slate-400 uppercase tracking-tighter">{s.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* === 6. MISIONES DEL DÍA === */}
      <div className="bg-gradient-to-br from-amber-400 via-orange-500 to-red-500 p-6 rounded-[3rem] text-white relative overflow-hidden shadow-2xl border-b-8 border-orange-700/40">
        <div className="absolute top-0 right-0 w-40 h-40 bg-white/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4 pointer-events-none" />
        <div className="relative z-10 space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-[10px] font-black uppercase tracking-[0.3em] opacity-80">Misiones del Día</p>
            <span className="bg-white/20 px-3 py-1 rounded-full text-xs font-black">
              {daily.completedQuests}/{daily.totalQuests}
            </span>
          </div>

          <div className="w-full bg-white/20 rounded-full h-2.5 overflow-hidden">
            <div className="h-full bg-white rounded-full transition-all duration-1000"
              style={{ width: `${daily.progressPercentage}%` }} />
          </div>

          <div className="space-y-2.5">
            {daily.quests.map((quest) => (
              <div key={quest.id} className={`flex items-center gap-3 p-4 rounded-2xl transition-all ${quest.isCompleted ? 'bg-white/30' : 'bg-white/10'}`}>
                <span className="text-2xl shrink-0">{quest.icon}</span>
                <div className="flex-1 text-left min-w-0">
                  <p className="font-black text-sm leading-none">{quest.title}</p>
                  <p className="text-[10px] opacity-80 mt-0.5 truncate">{quest.description}</p>
                  <div className="w-full bg-white/20 rounded-full h-1.5 mt-2 overflow-hidden">
                    <div className="h-full bg-white rounded-full transition-all duration-700"
                      style={{ width: `${Math.min((quest.current / quest.target) * 100, 100)}%` }} />
                  </div>
                </div>
                <div className="shrink-0">
                  {quest.isCompleted
                    ? <span className="text-xl">✅</span>
                    : <span className="text-[10px] font-black bg-white/20 px-2 py-1 rounded-lg">+{quest.rewardXP} XP</span>
                  }
                </div>
              </div>
            ))}
          </div>

          {daily.allCompleted && (
            <div className="bg-white/20 rounded-2xl p-4 text-center animate-in zoom-in">
              <p className="font-black text-lg">🏆 ¡Misiones Completadas!</p>
              <p className="text-xs opacity-80 mt-1">Vuelve mañana para nuevos retos</p>
            </div>
          )}
        </div>
      </div>

      {/* === 7. NIVEL CEFR === */}
      <div className="bg-indigo-50/50 p-6 rounded-[2.5rem] border-2 border-indigo-100 space-y-4 shadow-sm text-left">
        <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest">Mi Nivel Oficial (CEFR)</p>

        {userProgress?.cefrLevel ? (
          <>
            <div className="flex items-center gap-4 bg-white p-4 rounded-3xl border border-indigo-100 shadow-sm">
              <div className="w-20 h-20 rounded-2xl bg-indigo-600 flex items-center justify-center shadow-lg text-3xl font-black text-white shrink-0">
                {userProgress.cefrLevel}
              </div>
              <div>
                <p className="text-lg font-black text-slate-800 leading-tight">Dominio {userProgress.cefrLevel}</p>
                <p className="text-xs text-slate-400 font-bold mt-1">Evaluado el {userProgress.cefrTestDate}</p>
                {userProgress.cefrAccuracy && (
                  <div className="mt-2 flex items-center gap-1.5 text-indigo-600 font-black text-[10px] uppercase">
                    <Target size={12} /> {userProgress.cefrAccuracy}% Precisión
                  </div>
                )}
              </div>
            </div>

            {showLevelInfo && <LevelExplorer userLevel={userProgress.cefrLevel} />}

            <div className="grid grid-cols-2 gap-3">
              <button onClick={() => setView('placement-select')}
                className="py-3.5 rounded-2xl bg-white border-2 border-indigo-200 text-indigo-600 font-black text-xs hover:bg-indigo-50 transition-all active:scale-95 flex items-center justify-center gap-2">
                🔁 REPETIR TEST
              </button>
              <button onClick={() => { if (typeof setPlacementMode === 'function') setPlacementMode('master'); setView('placement-test'); }}
                className="py-3.5 rounded-2xl bg-purple-600 text-white font-black text-xs hover:bg-purple-700 transition-all active:scale-95 shadow-md flex items-center justify-center gap-2">
                🏆 MASTER CHALLENGE
              </button>
            </div>

            <button onClick={() => setShowLevelInfo(!showLevelInfo)}
              className="w-full py-3 rounded-xl text-slate-400 font-bold text-[11px] uppercase tracking-wider hover:text-indigo-500 transition-colors">
              {showLevelInfo ? 'Ocultar detalles' : '🔍 ¿Qué significa mi nivel?'}
            </button>
          </>
        ) : (
          <div className="bg-white p-6 rounded-3xl border-2 border-dashed border-indigo-200 flex flex-col items-center space-y-4">
            <p className="text-sm text-slate-500 font-medium text-center italic">Aún no has medido tu nivel de inglés oficial.</p>
            <button onClick={() => setView('placement-select')}
              className="w-full py-4 rounded-2xl bg-indigo-600 text-white font-black text-sm hover:bg-indigo-700 transition-all active:scale-95 shadow-lg">
              🎯 TOMAR PLACEMENT TEST
            </button>
          </div>
        )}
      </div>

      {/* === 8. DIPLOMA === */}
      {userProgress?.cefrLevel && (
        <button onClick={onGoToDiploma}
          className="w-full bg-gradient-to-r from-amber-400 to-yellow-500 p-5 rounded-[2rem] shadow-xl border-b-4 border-amber-600 text-white flex items-center justify-between px-8 hover:scale-[1.02] transition-transform active:scale-95">
          <div className="flex items-center gap-4">
            <div className="bg-white/20 p-2 rounded-xl"><Award size={28} /></div>
            <div className="text-left">
              <p className="font-black text-lg leading-none">Mi Diploma</p>
              <p className="text-[10px] font-bold opacity-90 uppercase tracking-wider mt-0.5">Descargar certificado oficial</p>
            </div>
          </div>
          <ChevronRight size={22} />
        </button>
      )}

      {/* === 9. AYUDA Y LOGOUT === */}
      <div className="space-y-3 pt-2">
        <button onClick={onOpenTutorial}
          className="w-full flex items-center justify-between p-5 rounded-3xl bg-white border-2 border-slate-100 text-slate-700 font-black hover:bg-slate-50 transition-all shadow-sm">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center text-xl">💡</div>
            <span className="text-sm">¿Cómo usar la App?</span>
          </div>
          <ChevronRight size={20} className="text-slate-300" />
        </button>

        <button onClick={handleLogout}
          className="w-full py-5 rounded-3xl border-b-4 border-slate-300 bg-white text-slate-600 font-black text-sm active:border-b-0 active:translate-y-1 transition-all flex items-center justify-center gap-2">
          <LogOut size={18} /> CERRAR SESIÓN
        </button>

        {isTeacherMode && (
          <button onClick={handleResetProgress}
            className="w-full py-5 rounded-3xl border-b-4 border-red-300 bg-red-50 text-red-500 font-black text-sm active:border-b-0 active:translate-y-1 transition-all flex items-center justify-center gap-2">
            <Trash2 size={18} /> BORRAR TODO EL PROGRESO
          </button>
        )}
      </div>

      {/* === 10. ZONA DE PROFESOR === */}
      <div className="pt-8 border-t border-slate-100">
        {!showAdminPanel ? (
          <button onClick={() => setShowAdminPanel(true)}
            className="text-[10px] font-black text-slate-300 hover:text-indigo-400 tracking-[0.3em] flex items-center gap-2 mx-auto transition-colors uppercase">
            <Lock size={12} /> Zona de Profesor
          </button>
        ) : (
          <div className="bg-slate-900 p-8 rounded-[3rem] space-y-6 animate-in zoom-in duration-300 text-white shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-500" />
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2 text-indigo-400 font-black text-[10px] tracking-widest uppercase">
                <ShieldCheck size={14} /> Sistema de Auditoría
              </div>
              <button onClick={() => setShowAdminPanel(false)} className="text-slate-500 hover:text-white transition-colors">
                <X size={18} />
              </button>
            </div>

            {isTeacherMode ? (
              <div className="space-y-3">
                <button onClick={() => onGrantTeacherXP?.()}
                  className="w-full py-5 bg-gradient-to-r from-amber-500 to-orange-600 rounded-3xl font-black text-sm shadow-lg active:scale-95 transition-all flex items-center justify-center gap-3">
                  <Sparkles size={20} /> AÑADIR XP DE AUDITORÍA
                </button>
                <button onClick={handleResetProgress}
                  className="w-full py-4 bg-red-500/10 text-red-400 rounded-3xl font-bold text-xs border border-red-500/20 hover:bg-red-500/20 transition-all">
                  HARD RESET (REINICIAR TODO)
                </button>
              </div>
            ) : (
              <div className="space-y-5">
                {mascotBubble && (
                  <div className="flex justify-center -mb-4">
                    <Mascot type={currentSkin} message={mascotBubble} />
                  </div>
                )}
                <input type="password" placeholder="Ingresar Key..." value={password}
                  onChange={(e) => { setPassword(e.target.value); if (mascotBubble) setMascotBubble(null); }}
                  onKeyDown={(e) => e.key === 'Enter' && handleUnlockAttempt()}
                  className="w-full bg-slate-800/50 border-2 border-slate-700 rounded-2xl p-4 text-center font-mono text-xl tracking-widest focus:border-indigo-500 outline-none text-white transition-all"
                />
                <button onClick={handleUnlockAttempt}
                  className="w-full py-4 bg-indigo-600 rounded-2xl font-black text-sm shadow-xl hover:bg-indigo-500 active:scale-95 transition-all">
                  DESBLOQUEAR TERMINAL
                </button>
              </div>
            )}
          </div>
        )}
      </div>
</div>
      {/* ═══════════════════════════════════════════ */}
      {/* OVERLAY 1: NIVELES                          */}
      {/* ═══════════════════════════════════════════ */}
      {showLevelOverlay && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end justify-center p-4"
          onClick={() => setShowLevelOverlay(false)}>
          <div className="bg-white w-full max-w-lg rounded-[2.5rem] p-6 space-y-4 animate-in slide-in-from-bottom-10 duration-300 max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center shrink-0">
              <div>
                <h3 className="font-black text-slate-800 text-lg">Sistema de Niveles</h3>
                <p className="text-xs text-slate-400 font-bold">Nivel {levelStats.level} · {levelStats.title}</p>
              </div>
              <button onClick={() => setShowLevelOverlay(false)}
                className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center text-slate-500 hover:bg-slate-200">
                <X size={16} />
              </button>
            </div>

            <div className="space-y-3 overflow-y-auto custom-scrollbar flex-1">
              {LEVEL_RANGES.map((tier, idx) => {
                const nextMin = LEVEL_RANGES[idx + 1]?.minLevel;
                const isCurrentTier = levelStats.level >= tier.minLevel && (nextMin ? levelStats.level < nextMin : true);
                const isCompleted = !isCurrentTier && levelStats.level >= tier.minLevel;

                return (
                  <div key={tier.range}
                    className={`rounded-2xl overflow-hidden border-2 transition-all ${isCurrentTier ? 'border-indigo-400 shadow-lg' : isCompleted ? 'border-slate-200 opacity-70' : 'border-slate-100 opacity-40'}`}>
                    <div className={`bg-gradient-to-r ${tier.color} p-3 px-4 flex justify-between items-center`}>
                      <div className="text-left">
                        <p className="font-black text-white text-sm">{tier.name}</p>
                        <p className="text-white/80 text-[10px] font-bold">Niveles {tier.range} · {tier.xp}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {isCurrentTier && <span className="bg-white text-indigo-600 text-[9px] font-black px-2 py-0.5 rounded-full">ACTUAL</span>}
                        {isCompleted && <span className="text-white text-lg">✅</span>}
                      </div>
                    </div>
                    <div className="bg-slate-50 px-4 py-2.5 flex flex-wrap gap-1.5">
                      {tier.titles.map((t) => (
                        <span key={t} className="text-[10px] font-bold text-slate-600 bg-white px-2.5 py-1 rounded-lg border border-slate-100">{t}</span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════ */}
      {/* OVERLAY 2: BADGES                           */}
      {/* ═══════════════════════════════════════════ */}
      {showBadgesOverlay && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end justify-center p-4"
          onClick={() => setShowBadgesOverlay(false)}>
          <div className="bg-white w-full max-w-lg rounded-[2.5rem] p-6 space-y-4 animate-in slide-in-from-bottom-10 duration-300 max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center shrink-0">
              <div>
                <h3 className="font-black text-slate-800 text-lg">Todos los Logros</h3>
                <p className="text-xs text-slate-400 font-bold">
                  {userBadges.filter(b => b.isUnlocked).length} de {userBadges.length} desbloqueados
                </p>
              </div>
              <button onClick={() => setShowBadgesOverlay(false)}
                className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center text-slate-500 hover:bg-slate-200">
                <X size={16} />
              </button>
            </div>

            <div className="space-y-3 overflow-y-auto custom-scrollbar flex-1">
              {userBadges.map((badge) => (
                <div key={badge.id}
                  className={`rounded-3xl border-2 p-4 flex items-center gap-4 transition-all ${badge.isUnlocked ? 'border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50' : 'border-slate-100 bg-slate-50 opacity-60'}`}>

                  <div className={`w-16 h-16 rounded-full flex items-center justify-center ring-4 shrink-0 relative overflow-hidden
  border-4 transition-all
  ${badge.isUnlocked
    ? `bg-gradient-to-br ${badge.currentGradient} ring-white border-yellow-300 shadow-[0_6px_20px_rgba(0,0,0,0.25)]`
    : 'bg-slate-200 ring-slate-100 border-slate-300 grayscale opacity-50'}`}>
  {/* Brillos solo si está desbloqueado */}
  {badge.isUnlocked && <>
    <div className="absolute top-0 left-0 w-full h-1/2 bg-white/40 rounded-t-full pointer-events-none" />
    <div className="absolute top-1 left-3 w-3 h-3 bg-white/60 rounded-full blur-sm pointer-events-none" />
  </>}
  <span className="text-2xl drop-shadow-md relative z-10">
    {badge.isUnlocked ? badge.icon : '🔒'}
  </span>
</div>

                  <div className="flex-1 text-left min-w-0">
                    <p className="font-black text-slate-800 text-sm">{badge.categoryName}</p>
                    <p className="text-[10px] text-slate-500 font-bold mt-0.5">{badge.description}</p>

                    {badge.isUnlocked ? (
                      <div className="mt-2 space-y-1">
                        <div className="flex justify-between items-center">
                          <span className="text-[10px] font-black text-amber-600 uppercase">{badge.currentTierName}</span>
                          {!badge.isMaxedOut && <span className="text-[9px] text-slate-400 font-bold">{badge.progressText}</span>}
                        </div>
                        {!badge.isMaxedOut && (
                          <>
                            <div className="w-full h-1.5 bg-slate-200 rounded-full overflow-hidden">
                              <div className={`h-full bg-gradient-to-r ${badge.currentGradient} rounded-full transition-all`}
                                style={{ width: `${badge.progressToNext}%` }} />
                            </div>
                            <p className="text-[9px] text-slate-400">Siguiente: <span className="font-black text-slate-600">{badge.nextTierName}</span></p>
                          </>
                        )}
                        {badge.isMaxedOut && <p className="text-[9px] font-black text-amber-500 uppercase">🏆 ¡Nivel máximo!</p>}
                      </div>
                    ) : (
                      <p className="text-[10px] text-slate-400 mt-1.5 italic">Sigue jugando para desbloquear</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
 </>   
  );
}
